import { AsyncPipe } from '@angular/common';
import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  Output,
  signal,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { TuiTextfieldComponent, TuiTextfield, TuiLabel } from '@taiga-ui/core';
import { TuiChevron, TuiComboBox, TuiDataListWrapper, TuiFilterByInputPipe } from '@taiga-ui/kit';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { IDropdown } from '../../../shared/types/dropdown.interface';
import { LoadingComponent } from '../loading/loading.component';

/** Componente de select integrado com Reactive Forms. */
@Component({
  selector: 'app-select-with-ngform',
  standalone: true,
  imports: [
    TuiTextfieldComponent,
    TuiTextfield,
    TuiLabel,
    TuiComboBox,
    TuiDataListWrapper,
    TuiChevron,
    LoadingComponent,
    ReactiveFormsModule,
    FormsModule,
    TuiTextfield,
    TuiFilterByInputPipe,
    AsyncPipe,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectWithNgformComponent),
      multi: true,
    },
  ],
  host: {
    '(blur)': 'onTouched()',
    '[attr.aria-label]': 'placeholder',
  },
  templateUrl: './select-with-ngform.component.html',
  styleUrl: './select-with-ngform.component.scss',
})
export class SelectWithNgformComponent implements ControlValueAccessor, OnChanges {
  @Input() placeholder = '';
  @Input() label = '';
  @Input({ required: true }) items: IDropdown[] = [];
  @Input() isLoading = false;
  @Input() labelField = false;
  @Input() clearButton = true;
  @Input() size: 'm' | 'l' | 's' = 'm';
  @Input() showStartButton? = false;
  @Input() darkField? = false;
  @Output() valueSelect = new EventEmitter<string | null>();

  private _value = signal<string | null>(null);
  displayText = signal<string>('');
  private isDisabled = signal(false);

  private items$ = new BehaviorSubject<IDropdown[]>([]);

  public get value(): string | null {
    return this._value();
  }

  public get disabled(): boolean {
    return this.isDisabled();
  }

  ngOnChanges(): void {
    this.items$.next(this.items);

    // Atualiza display quando items chegarem
    if (this._value()) {
      const item = this.items.find((i) => i.value === this._value());
      this.displayText.set(item?.label ?? '');
    }
  }

  get itemsAsStrings$(): Observable<string[]> {
    return this.items$.pipe(map((items) => items.map((item) => item.label)));
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function, @typescript-eslint/no-unused-vars
  onChange = (v: string | null) => {};

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouched = () => {};

  writeValue(val: string | null): void {
    this._value.set(val);

    const item = this.items.find((i) => i.value === val);
    this.displayText.set(item?.label ?? '');
  }

  registerOnChange(fn: (v: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  updateValue(label: string): void {
    const item = this.items.find((i) => i.label === label);

    const realValue = item?.value ?? null;

    this._value.set(realValue);
    this.onChange(realValue);
    this.valueSelect.emit(realValue);
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }
}
